import { apiFetch, apiUploadFormData } from "@/lib/api";

export interface SuntronicInvoiceShipmentRow {
  shipment_id: number;
  batch_id: number | null;
  sku: string;
  date_shipped: string | null;
  shipment_quantity: number | string;
  total_matched_quantity: number | string | null;
  remaining_quantity: number | string | null;
  build_order_id: number | null;
  batch_item_id: number | null;
  location_id: number | string | null;
  location_name: string | null;
}

export interface SuntronicInvoiceFinishedGoodRow {
  id: number;
  sku: string;
  quantity: number | string;
  quantity_matched: number | string | null;
  mv_build_order_number: string | null;
  number: string | null;
  created: string | null;
  service_cost: number | string | null;
  megaventory_id: number | null;
  build_order_id: number | null;
}

export interface SuntronicInvoiceOptionRow {
  label: string;
  value: number;
  total: string;
  manufacturer: string;
  number: string;
  date: string | null;
  total_cost_raw: string;
  reconciled: boolean;
}

export interface SuntronicInvoiceMatchRow {
  id: number;
  mfr_invoice_id: number;
  finished_good_id: number;
  quantity: number | string;
  batch_id: number | null;
  shipment_id: number | null;
  batch_item_id: number | null;
  purchase_order_item_id: number | null;
  finished_good_number: string | null;
  build_order_id: number | string | null;
  sku: string | null;
  service_cost: number | string | null;
  tla_sku: string | null;
  tla_order_numbers: string | null;
}

export interface SuntronicInvoiceGipiPurchaseOrderRow {
  id: number;
  po_number: string | null;
  system_of_record: string | null;
  supplier_name: string | null;
  quantity_ordered: number | string;
  quantity_received: number | string;
  quantity_remaining_to_invoice: number | string;
  associated_with_matches: boolean;
}

export interface SuntronicInvoiceGipiPurchaseOrderLineRow {
  purchase_order_item_id: number;
  sku: string | null;
  description: string | null;
  quantity_ordered: number | string;
  quantity_invoiced: number | string;
  quantity_remaining: number | string;
  unit_price: number | string | null;
  matched_quantity: number | string;
  suggested_quantity: number | string;
}

export interface SuntronicInvoiceGipiCandidateRow {
  reference: string | null;
  id: number;
  mv_number: string | null;
  invoice_value: number | string | null;
}

export interface SuntronicInvoiceWorkOrderRow {
  WorkOrderNo: string;
  WorkOrderFinishedGoodSKU: string;
  WorkOrderStatus: string;
  WorkOrderOrderedQuantity: number;
  WorkOrderReceivedQuantity: number;
  WorkOrderCreationDate: string;
  WorkOrderInventoryLocationID: number;
  WorkOrderReferenceNo: string;
  WorkOrderComments?: string;
  WorkOrderId: number;
  sb_id: number;
  sb_number: string;
  sb_mv_number: string | null;
  sb_stocking: boolean | null;
  sb_mfr_reference_number: string | null;
}

export async function fetchSuntronicInvoiceShipments(): Promise<SuntronicInvoiceShipmentRow[]> {
  const data = await apiFetch<{ rows: SuntronicInvoiceShipmentRow[] }>("/api/accounting/suntronic-invoices/shipments");
  return data.rows;
}

export async function fetchSuntronicInvoiceFinishedGoods(
  buildOrderId: number,
): Promise<SuntronicInvoiceFinishedGoodRow[]> {
  const params = new URLSearchParams({
    build_order_id: String(buildOrderId),
  });
  const data = await apiFetch<{ rows: SuntronicInvoiceFinishedGoodRow[] }>(
    `/api/accounting/suntronic-invoices/finished-goods?${params.toString()}`,
  );
  return data.rows;
}

function coerceInvoiceOptionRow(row: SuntronicInvoiceOptionRow): SuntronicInvoiceOptionRow {
  return {
    ...row,
    value: Number(row.value),
    reconciled: Boolean(row.reconciled),
  };
}

export async function fetchSuntronicInvoiceOptions(
  includeReconciled = false,
): Promise<SuntronicInvoiceOptionRow[]> {
  const params = new URLSearchParams();
  if (includeReconciled) {
    params.set("include_reconciled", "1");
  }
  const query = params.toString();
  const data = await apiFetch<{ rows: SuntronicInvoiceOptionRow[] }>(
    `/api/accounting/suntronic-invoices/invoices${query === "" ? "" : `?${query}`}`,
  );
  return data.rows.map(coerceInvoiceOptionRow);
}

export async function fetchSuntronicInvoiceMatches(invoiceId: number): Promise<SuntronicInvoiceMatchRow[]> {
  const data = await apiFetch<{ rows: SuntronicInvoiceMatchRow[] }>(
    `/api/accounting/suntronic-invoices/invoices/${encodeURIComponent(String(invoiceId))}`,
  );
  return data.rows;
}

export interface SuntronicInvoiceDocumentRow {
  id: number;
  filename: string;
  content_type?: string;
  uploaded_at?: string;
}

export async function fetchSuntronicInvoiceDocuments(invoiceId: number): Promise<SuntronicInvoiceDocumentRow[]> {
  const data = await apiFetch<{ rows: SuntronicInvoiceDocumentRow[] }>(
    `/api/accounting/suntronic-invoices/invoices/${encodeURIComponent(String(invoiceId))}/documents`,
  );
  return data.rows.map((row) => ({
    ...row,
    id: Number(row.id),
  }));
}

export async function fetchSuntronicInvoiceDocumentUrl(
  invoiceId: number,
  documentId: number,
): Promise<{ signedUrl: string; filename: string; contentType?: string | null }> {
  return apiFetch<{ signedUrl: string; filename: string; contentType?: string | null }>(
    `/api/accounting/suntronic-invoices/invoices/${encodeURIComponent(String(invoiceId))}/documents/${encodeURIComponent(String(documentId))}`,
  );
}

export async function uploadSuntronicInvoiceDocuments(
  invoiceId: number,
  files: File[],
): Promise<SuntronicInvoiceDocumentRow[]> {
  const formData = new FormData();
  for (const file of files) {
    formData.append("files", file);
  }
  const data = await apiUploadFormData<{ documents?: SuntronicInvoiceDocumentRow[] }>(
    `/api/accounting/suntronic-invoices/invoices/${encodeURIComponent(String(invoiceId))}/documents`,
    formData,
  );
  return (data.documents ?? []).map((row) => ({
    ...row,
    id: Number(row.id),
  }));
}

export async function deleteSuntronicInvoiceDocument(invoiceId: number, documentId: number): Promise<void> {
  await apiFetch(
    `/api/accounting/suntronic-invoices/invoices/${encodeURIComponent(String(invoiceId))}/documents/${encodeURIComponent(String(documentId))}`,
    { method: "DELETE" },
  );
}

export async function createSuntronicInvoice(input: {
  manufacturer: string;
  mfr_invoice_number: string;
  total_cost: number;
  date: string;
  files?: File[];
}): Promise<{ id: number; documents: SuntronicInvoiceDocumentRow[] }> {
  const files = input.files ?? [];
  if (files.length > 0) {
    const formData = new FormData();
    formData.append("manufacturer", input.manufacturer);
    formData.append("mfr_invoice_number", input.mfr_invoice_number);
    formData.append("total_cost", String(input.total_cost));
    formData.append("date", input.date);
    for (const file of files) {
      formData.append("files", file);
    }
    const data = await apiUploadFormData<{
      invoice: { id: number };
      documents?: SuntronicInvoiceDocumentRow[];
    }>("/api/accounting/suntronic-invoices/invoices", formData);
    return { id: data.invoice.id, documents: data.documents ?? [] };
  }
  const data = await apiFetch<{ invoice: { id: number }; documents?: SuntronicInvoiceDocumentRow[] }>(
    "/api/accounting/suntronic-invoices/invoices",
    {
      method: "POST",
      body: {
        manufacturer: input.manufacturer,
        mfr_invoice_number: input.mfr_invoice_number,
        total_cost: input.total_cost,
        date: input.date,
      },
    },
  );
  return { id: data.invoice.id, documents: data.documents ?? [] };
}

export async function patchSuntronicInvoiceDate(
  invoiceId: number,
  date: string,
): Promise<{ invoice_id: number; date: string }> {
  return apiFetch(`/api/accounting/suntronic-invoices/invoices/${encodeURIComponent(String(invoiceId))}`, {
    method: "PATCH",
    body: { date },
  });
}

export async function createSuntronicInvoiceMatch(input: {
  mfr_invoice_id: number;
  finished_good_id: number;
  quantity: number;
  batch_id?: number | null;
  shipment_id?: number | null;
  batch_item_id?: number | null;
  purchase_order_item_id?: number | null;
}): Promise<void> {
  await apiFetch("/api/accounting/suntronic-invoices/invoice-matches", {
    method: "POST",
    body: input,
  });
}

export async function deleteSuntronicInvoiceMatch(matchId: number): Promise<void> {
  await apiFetch(`/api/accounting/suntronic-invoices/invoice-matches/${encodeURIComponent(String(matchId))}`, {
    method: "DELETE",
  });
}

export async function updateSuntronicInvoiceMatchQuantity(matchId: number, quantity: number): Promise<void> {
  await apiFetch(`/api/accounting/suntronic-invoices/invoice-matches/${encodeURIComponent(String(matchId))}/quantity`, {
    method: "PATCH",
    body: { quantity },
  });
}

export async function updateSuntronicFinishedGoodServiceCost(finishedGoodId: number, serviceCost: number): Promise<void> {
  await apiFetch(
    `/api/accounting/suntronic-invoices/finished-goods/${encodeURIComponent(String(finishedGoodId))}/service-cost`,
    {
      method: "PATCH",
      body: { service_cost: serviceCost },
    },
  );
}

export async function fetchSuntronicInvoiceGipiPurchaseOrders(
  invoiceId: number,
): Promise<SuntronicInvoiceGipiPurchaseOrderRow[]> {
  const data = await apiFetch<{ rows: SuntronicInvoiceGipiPurchaseOrderRow[] }>(
    `/api/accounting/suntronic-invoices/invoices/${encodeURIComponent(String(invoiceId))}/gipi-purchase-orders`,
  );
  return data.rows;
}

export async function fetchSuntronicInvoiceGipiPurchaseOrderLines(
  invoiceId: number,
  purchaseOrderId: number,
): Promise<SuntronicInvoiceGipiPurchaseOrderLineRow[]> {
  const data = await apiFetch<{ rows: SuntronicInvoiceGipiPurchaseOrderLineRow[] }>(
    `/api/accounting/suntronic-invoices/invoices/${encodeURIComponent(String(invoiceId))}/gipi-purchase-orders/${encodeURIComponent(String(purchaseOrderId))}/lines`,
  );
  return data.rows;
}

export async function createSuntronicInvoiceGipi(
  invoiceId: number,
  purchaseOrderId: number,
  lines: Array<{ purchase_order_item_id: number; quantity: number }>,
): Promise<{ mv_document_id: number; purchase_invoice_id: number }> {
  return apiFetch(`/api/accounting/suntronic-invoices/invoices/${encodeURIComponent(String(invoiceId))}/create-gipi`, {
    method: "POST",
    body: { purchase_order_id: purchaseOrderId, lines },
  });
}

export async function fetchSuntronicInvoiceGipiCandidates(invoiceId: number): Promise<SuntronicInvoiceGipiCandidateRow[]> {
  const data = await apiFetch<{ rows: SuntronicInvoiceGipiCandidateRow[] }>(
    `/api/accounting/suntronic-invoices/invoices/${encodeURIComponent(String(invoiceId))}/gipi-candidates`,
  );
  return data.rows;
}

export interface SuntronicInvoiceLinkedGipiRow {
  purchase_invoice_id: number;
  mv_number: string | null;
  vendor_invoice_number: string | null;
  payment_terms: string | null;
  expected_due_date: string | null;
  invoice_total: number | string;
}

export async function fetchSuntronicInvoiceLinkedGipis(invoiceId: number): Promise<SuntronicInvoiceLinkedGipiRow[]> {
  const data = await apiFetch<{ rows: SuntronicInvoiceLinkedGipiRow[] }>(
    `/api/accounting/suntronic-invoices/invoices/${encodeURIComponent(String(invoiceId))}/linked-gipis`,
  );
  return data.rows;
}

export async function linkSuntronicInvoiceGipis(invoiceId: number, purchaseInvoiceIds: number[]): Promise<void> {
  await apiFetch(`/api/accounting/suntronic-invoices/invoices/${encodeURIComponent(String(invoiceId))}/link-gipis`, {
    method: "POST",
    body: { purchase_invoice_ids: purchaseInvoiceIds },
  });
}

export async function sendSuntronicInvoiceToZapier(invoiceId: number): Promise<void> {
  await apiFetch(`/api/accounting/suntronic-invoices/invoices/${encodeURIComponent(String(invoiceId))}/send-to-zapier`, {
    method: "POST",
  });
}

export async function fetchSuntronicInvoiceWorkOrders(hideGso: boolean): Promise<SuntronicInvoiceWorkOrderRow[]> {
  const params = new URLSearchParams({
    hide_gso: hideGso ? "1" : "0",
  });
  const data = await apiFetch<{ rows: SuntronicInvoiceWorkOrderRow[] }>(
    `/api/accounting/suntronic-invoices/work-orders?${params.toString()}`,
    { signal: AbortSignal.timeout(20_000) },
  );
  return data.rows;
}
